import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MailCheckIcon, MailIcon } from 'lucide-react';
import {
  AuthShell,
  BrandMark,
  Button,
  Callout,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  IconInput,
  ResultState,
} from '@opengoal/ui';
import { normalizeAuthError } from '@opengoal/http';
import { useTranslation } from 'react-i18next';
import { env } from '@/app/env';
import { authClient } from '@/lib/auth-client';

export function ForgotPasswordPage() {
  const { t } = useTranslation();

  const schema = z.object({
    email: z.string().trim().min(1, t('auth.emailRequired')).email(t('auth.emailInvalid')),
  });
  type ForgotPasswordFormValues = z.infer<typeof schema>;

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const requestReset = useMutation({
    mutationFn: async (values: ForgotPasswordFormValues) => {
      const { error } = await authClient.requestPasswordReset({
        email: values.email,
        // Where the emailed link lands. The backend builds the full URL from
        // ADMIN_APP_BASE_URL, so this is the path within this app.
        redirectTo: '/reset-password',
      });
      if (error) throw normalizeAuthError(error);
    },
  });

  return (
    <AuthShell
      brand={<BrandMark name={env.appName} tagline={t('app.tagline')} />}
      title={t('auth.forgotTitle')}
      subtitle={t('auth.forgotSubtitle')}
      footer={
        <Link to="/login" className="text-muted-foreground hover:text-foreground">
          {t('auth.backToSignIn')}
        </Link>
      }
    >
      {requestReset.isSuccess ? (
        // Deliberately identical whether or not the address has an account:
        // a "no such user" message would turn this form into a way of testing
        // which email addresses are registered.
        <ResultState
          tone="success"
          icon={<MailCheckIcon />}
          title={t('auth.resetLinkSentTitle')}
          description={t('auth.resetLinkSentBody')}
          className="px-0 py-2"
        />
      ) : (
        <Form {...form}>
          <form
            noValidate
            className="grid gap-5"
            onSubmit={form.handleSubmit((values) => requestReset.mutate(values))}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.email')}</FormLabel>
                  <FormControl>
                    <IconInput
                      icon={<MailIcon />}
                      type="email"
                      autoComplete="email"
                      className="h-10"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {requestReset.isError ? (
              <Callout variant="destructive" title={t('auth.errors.generic')} />
            ) : null}

            <Button type="submit" size="lg" className="w-full" loading={requestReset.isPending}>
              {t('auth.sendResetLink')}
            </Button>
          </form>
        </Form>
      )}
    </AuthShell>
  );
}
